import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Edit, Trash2, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Clients = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);

  useEffect(() => {
    loadClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const filtered = clients.filter((client) => {
      const searchLower = searchTerm.toLowerCase();
      const name = client.razon_social || `${client.primer_nombre} ${client.primer_apellido}`;
      const nit = client.nit || client.numero_documento || '';
      const ciudad = client.ciudad || '';

      return (
        name.toLowerCase().includes(searchLower) ||
        nit.includes(searchLower) ||
        ciudad.toLowerCase().includes(searchLower)
      );
    });
    setFilteredClients(filtered);
  }, [searchTerm, clients]);

  const loadClients = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/clients`);
      setClients(data);
      setFilteredClients(data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm('¿Está seguro de eliminar este cliente?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/clients/${clientId}`);
      loadClients();
    } catch (error) {
      alert('Error al eliminar el cliente');
    }
  };

  const getEstadoBadge = (estado) => {
    const badges = {
      Activo: 'bg-emerald-100 text-emerald-800',
      Pausado: 'bg-indigo-100 text-indigo-800',
      Inactivo: 'bg-slate-100 text-slate-800',
    };
    return badges[estado] || 'bg-slate-100 text-slate-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-600">Cargando clientes...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl tracking-tight font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
            Clientes
          </h1>
          <p className="text-sm text-slate-700 leading-relaxed mt-2">Gestión de clientes y sus datos fiscales</p>
        </div>
        {(user?.role === 'Administrador' || user?.role === 'Contador') && (
          <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
            <DialogTrigger asChild>
              <Button
                className="bg-[#1F3864] hover:bg-[#172b4d] text-white"
                data-testid="create-client-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Cliente
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Crear Nuevo Cliente</DialogTitle>
              </DialogHeader>
              <ClientForm onSuccess={() => { setShowCreateModal(false); loadClients(); }} />
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Search */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            type="text"
            placeholder="Buscar por nombre, NIT o ciudad..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-clients-input"
          />
        </div>
      </div>

      {/* Clients Table */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="text-xs font-semibold tracking-wider uppercase text-slate-500 border-b border-slate-200 bg-slate-50">
              <tr>
                <th className="px-6 py-3">Nombre / Razón Social</th>
                <th className="px-6 py-3">NIT / Documento</th>
                <th className="px-6 py-3">Ciudad</th>
                <th className="px-6 py-3">Estado</th>
                <th className="px-6 py-3">Vencimientos Pendientes</th>
                <th className="px-6 py-3">Acciones</th>
              </tr>
            </thead>
            <tbody className="text-sm text-slate-700">
              {filteredClients.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-slate-500">                    No se encontraron clientes
                  </td>
                </tr>
              ) : (
                filteredClients.map((client) => (
                  <tr key={client._id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-6 py-4 font-medium text-slate-900">
                      {client.razon_social || `${client.primer_nombre || ''} ${client.primer_apellido || ''}`.trim()}
                    </td>
                    <td className="px-6 py-4">
                      {client.nit ? `${client.nit}-${client.dv}` : client.numero_documento}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">{client.responsable_name || 'Sin asignar'}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getEstadoBadge(client.estado)}`}>
                        {client.estado}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-semibold">{client.pending_deadlines || 0}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/clients/${client._id}`)}
                          data-testid={`view-client-${client._id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {(user?.role === 'Administrador' || user?.role === 'Contador') && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditingClient(client)}
                              data-testid={`edit-client-${client._id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            {user?.role === 'Administrador' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(client._id)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={`delete-client-${client._id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Client Modal */}
      <Dialog open={!!editingClient} onOpenChange={(open) => { if (!open) setEditingClient(null); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Cliente</DialogTitle>
          </DialogHeader>
          {editingClient && (
            <ClientForm
              client={editingClient}
              onSuccess={() => { setEditingClient(null); loadClients(); }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Client Form Component
const ClientForm = ({ client, onSuccess }) => {
  const [tipoPersona, setTipoPersona] = useState(client?.tipo_persona || 'Jurídica');
  const [formData, setFormData] = useState({
    razon_social: client?.razon_social || '',
    nit: client?.nit || '',
    dv: client?.dv || '',
    primer_nombre: client?.primer_nombre || '',
    segundo_nombre: client?.segundo_nombre || '',
    primer_apellido: client?.primer_apellido || '',
    segundo_apellido: client?.segundo_apellido || '',
    tipo_documento: client?.tipo_documento || 'CC',
    numero_documento: client?.numero_documento || '',
    email: client?.email || '',
    telefono_principal: client?.telefono_principal || '',
    telefono_secundario: client?.telefono_secundario || '',
    departamento: client?.departamento || '',
    ciudad: client?.ciudad || '',
    direccion: client?.direccion || '',
    representante_legal_nombre: client?.representante_legal_nombre || '',
    representante_legal_cedula: client?.representante_legal_cedula || '',
    representante_legal_cargo: client?.representante_legal_cargo || '',
    responsabilidades_fiscales: client?.responsabilidades_fiscales || [],
    actividades_economicas: client?.actividades_economicas || client?.actividad_economica ? (client?.actividades_economicas || [client.actividad_economica]).filter(Boolean) : [],
    periodicidad_iva: client?.periodicidad_iva || '',
    periodicidad_ica: client?.periodicidad_ica || '',
    es_agente_retencion: client?.es_agente_retencion || false,
    declara_renta: client?.declara_renta !== undefined ? client.declara_renta : true,
    regimen_simple: client?.regimen_simple || false,
    estado: client?.estado || 'Activo',
  });
  const [departamentos, setDepartamentos] = useState([]);
  const [ciudades, setCiudades] = useState([]);
  const [ciiuCodes, setCiiuCodes] = useState([]);
  const [ciiuSearch, setCiiuSearch] = useState('');
  const [ciiuFiltered, setCiiuFiltered] = useState([]);
  const [showCiiuDropdown, setShowCiiuDropdown] = useState(false);
  const ciiuRef = useRef(null);
  const [error, setError] = useState('');

  // Close CIIU dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ciiuRef.current && !ciiuRef.current.contains(e.target)) {
        setShowCiiuDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    loadDepartamentos();
    loadCiiuCodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (formData.departamento) {
      loadCiudades(formData.departamento);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.departamento]);

  useEffect(() => {
    if (tipoPersona === 'Jurídica' && formData.nit) {
      calculateDV(formData.nit);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.nit, tipoPersona]);

  const loadDepartamentos = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/utils/departamentos`);
      setDepartamentos(data);
    } catch (error) {
    }
  };

  const loadCiudades = async (departamento) => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/utils/ciudades/${departamento}`);
      setCiudades(data);
    } catch (error) {
    }
  };

  const loadCiiuCodes = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/utils/ciiu`);
      setCiiuCodes(data);
      setCiiuFiltered(data.slice(0, 30));
    } catch (error) {
    }
  };

  const handleCiiuSearch = (value) => {
    setCiiuSearch(value);
    setShowCiiuDropdown(true);
    if (!value.trim()) {
      setCiiuFiltered(ciiuCodes.slice(0, 30));
      return;
    }
    const q = value.toLowerCase();
    const filtered = ciiuCodes.filter(
      c => c.codigo.includes(q) || c.descripcion.toLowerCase().includes(q)
    );
    setCiiuFiltered(filtered.slice(0, 30));
  };

  const selectCiiu = (code) => {
    setFormData(prev => {
      const current = prev.actividades_economicas || [];
      if (current.includes(code.codigo)) return prev;
      return { ...prev, actividades_economicas: [...current, code.codigo] };
    });
    setCiiuSearch('');
    setShowCiiuDropdown(false);
  };

  const removeCiiu = (codigo) => {
    setFormData(prev => ({
      ...prev,
      actividades_economicas: (prev.actividades_economicas || []).filter(c => c !== codigo),
    }));
  };

  const getCiiuDescription = (codigo) => {
    const match = ciiuCodes.find(c => c.codigo === codigo);
    return match ? match.descripcion : codigo;
  };

  const calculateDV = async (nit) => {
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/utils/calculate-dv`, { nit });
      setFormData((prev) => ({ ...prev, dv: data.dv }));
    } catch (error) {
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleResponsabilidadToggle = (codigo) => {
    setFormData((prev) => {
      const current = prev.responsabilidades_fiscales || [];
      if (current.includes(codigo)) {
        return { ...prev, responsabilidades_fiscales: current.filter((c) => c !== codigo) };
      } else {
        return { ...prev, responsabilidades_fiscales: [...current, codigo] };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const payload = {
        ...formData,
        tipo_persona: tipoPersona,
      };

      if (client) {
        await axios.put(`${BACKEND_URL}/api/clients/${client._id}`, payload);
      } else {
        await axios.post(`${BACKEND_URL}/api/clients`, payload);
      }

      onSuccess();
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al guardar el cliente');
    }
  };

  const responsabilidades = [
    { codigo: '05', nombre: 'Impuesto de renta y complementarios régimen ordinario' },
    { codigo: '07', nombre: 'Retención en la fuente a título de renta' },
    { codigo: '09', nombre: 'Retención en la fuente en el impuesto sobre las ventas' },
    { codigo: '14', nombre: 'Informante de exógena' },
    { codigo: '42', nombre: 'Obligado a llevar contabilidad' },
    { codigo: '47', nombre: 'Régimen simple de tributación SIMPLE' },
    { codigo: '48', nombre: 'Impuesto sobre las ventas IVA' },
    { codigo: '49', nombre: 'No responsable de IVA' },
    { codigo: '52', nombre: 'Facturador electrónico' },
    { codigo: '55', nombre: 'Informante de beneficiarios finales' },
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="client-form">
      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
          {error}
        </div>
      )}

      {/* Tipo Persona */}
      <div>
        <label className="text-xs font-semibold tracking-wider uppercase text-slate-500 block mb-2">
          Tipo de Persona
        </label>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="tipo_persona"
              value="Jurídica"
              checked={tipoPersona === 'Jurídica'}
              onChange={(e) => setTipoPersona(e.target.value)}
              className="w-4 h-4"
            />
            <span className="text-sm">Persona Jurídica</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="tipo_persona"
              value="Natural"
              checked={tipoPersona === 'Natural'}
              onChange={(e) => setTipoPersona(e.target.value)}
              className="w-4 h-4"
            />
            <span className="text-sm">Persona Natural</span>
          </label>
        </div>
      </div>

      {/* Identificación */}
      {tipoPersona === 'Jurídica' ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <label className="text-xs font-semibold tracking-wider uppercase text-slate-500 block mb-1">
              Razón Social *
            </label>
            <Input
              name="razon_social"
              value={formData.razon_social}
              onChange={handleChange}
              required
              placeholder="ABC Inversiones S.A.S"
            />
          </div>
          <div>
            <label className="text-xs font-semibold tracking-wider uppercase text-slate-500 block mb-1">
              NIT *
            </label>
            <div className="flex gap-2">
              <Input
                name="nit"
                value={formData.nit}
                onChange={handleChange}
                required
                placeholder="900123456"
                className="flex-1"
              />
              <Input value={formData.dv} readOnly className="w-16" placeholder="DV" />
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold tracking-wider uppercase text-slate-500 block mb-1">
              Primer Nombre *
            </label>
            <Input
              name="primer_nombre"
              value={formData.primer_nombre}
              onChange={handleChange}
              required
            />
          </div>
          <div>
            <label className="text-xs font-semibold tracking-wider uppercase text-slate-500 block mb-1">
              Segundo Nombre
            </label>
            <Input
              name="segundo_nombre"
              value={formData.segundo_nombre}
              onChange={handleChange}
            />
          </div>
          <div>
            <label className="text-xs font-semibold tracking-wider uppercase text-slate-500 block mb-1">
              Primer Apellido *
            </label>
            <Input
              name="primer_apellido"
              value={formData.primer_apellido}
              onChange={handleChange}
              required
            />
          </div>
          <div>
            <label className="text-xs font-semibold tracking-wider uppercase text-slate-500 block mb-1">
              Segundo Apellido
            </label>
            <Input
              name="segundo_apellido"
              value={formData.segundo_apellido}
              onChange={handleChange}
            />
          </div>
          <div>
            <label className="text-xs font-semibold tracking-wider uppercase text-slate-500 block mb-1">
              Tipo Documento *
            </label>
            <select
              name="tipo_documento"
              value={formData.tipo_documento}
              onChange={handleChange}
              className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
              required
            >
              <option value="CC">Cédula de Ciudadanía</option>
              <option value="CE">Cédula de Extranjería</option>
              <option value="Pasaporte">Pasaporte</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold tracking-wider uppercase text-slate-500 block mb-1">
              Número Documento *
            </label>
            <Input
              name="numero_documento"
              value={formData.numero_documento}
              onChange={handleChange}
              required
            />
          </div>
        </div>
      )}

      {/* Contacto */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="text-xs font-semibold tracking-wider uppercase text-slate-500 block mb-1">
            Email *
          </label>
          <Input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </div>
        <div>
          <label className="text-xs font-semibold tracking-wider uppercase text-slate-500 block mb-1">
            Teléfono Principal *
          </label>
          <Input
            name="telefono_principal"
            value={formData.telefono_principal}
            onChange={handleChange}
            required
          />
        </div>
        <div>
          <label className="text-xs font-semibold tracking-wider uppercase text-slate-500 block mb-1">
            Teléfono Secundario
          </label>
          <Input
            name="telefono_secundario"
            value={formData.telefono_secundario}
            onChange={handleChange}
          />
        </div>
      </div>

      {/* Ubicación */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="text-xs font-semibold tracking-wider uppercase text-slate-500 block mb-1">
            Departamento *
          </label>
          <select
            name="departamento"
            value={formData.departamento}
            onChange={handleChange}
            className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
            required
          >
            <option value="">Seleccionar...</option>
            {departamentos.map((dep) => (
              <option key={dep} value={dep}>
                {dep}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold tracking-wider uppercase text-slate-500 block mb-1">
            Ciudad *
          </label>
          <select
            name="ciudad"
            value={formData.ciudad}
            onChange={handleChange}
            className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
            required
            disabled={!formData.departamento}
          >
            <option value="">Seleccionar...</option>
            {ciudades.map((city) => (
              <option key={city} value={city}>
                {city}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold tracking-wider uppercase text-slate-500 block mb-1">
            Dirección *
          </label>
          <Input
            name="direccion"
            value={formData.direccion}
            onChange={handleChange}
            required
          />
        </div>
      </div>

      {/* Actividad Económica CIIU */}
      <div className="relative" ref={ciiuRef}>
        <label className="text-xs font-semibold tracking-wider uppercase text-slate-500 block mb-1">
          Actividades Económicas (Códigos CIIU)
        </label>
        {/* Selected tags */}
        {formData.actividades_economicas?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2" data-testid="ciiu-tags">
            {formData.actividades_economicas.map((codigo) => (
              <span
                key={codigo}
                className="inline-flex items-center gap-1 px-2.5 py-1 bg-slate-100 border border-slate-200 rounded text-xs text-slate-700"
              >
                <strong>{codigo}</strong>
                <span className="hidden sm:inline">- {getCiiuDescription(codigo)}</span>
                <button
                  type="button"
                  onClick={() => removeCiiu(codigo)}
                  className="ml-1 text-slate-400 hover:text-red-500 font-bold"
                  data-testid={`remove-ciiu-${codigo}`}
                >
                  x
                </button>
              </span>
            ))}
          </div>
        )}
        <Input
          value={ciiuSearch}
          onChange={(e) => handleCiiuSearch(e.target.value)}
          onFocus={() => setShowCiiuDropdown(true)}
          placeholder="Buscar por código o descripción para agregar..."
          data-testid="ciiu-search-input"
        />
        {showCiiuDropdown && ciiuFiltered.length > 0 && (
          <div
            className="absolute z-50 mt-1 w-full max-h-48 overflow-y-auto bg-white border border-slate-300 rounded-md shadow-lg"
            data-testid="ciiu-dropdown"
          >
            {ciiuFiltered.map((code) => {
              const isSelected = formData.actividades_economicas?.includes(code.codigo);
              return (
                <button
                  key={code.codigo}
                  type="button"
                  onClick={() => selectCiiu(code)}
                  className={`w-full text-left px-3 py-2 text-sm border-b border-slate-100 last:border-b-0 ${
                    isSelected ? 'bg-blue-50 text-blue-700' : 'text-slate-700 hover:bg-blue-50'
                  }`}
                  disabled={isSelected}
                >
                  <span className="font-semibold text-slate-900">{code.codigo}</span>
                  <span className="mx-1.5 text-slate-400">-</span>
                  <span>{code.descripcion}</span>
                  {isSelected && <span className="ml-2 text-blue-500 text-xs">(agregado)</span>}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Representante Legal */}
      {tipoPersona === 'Jurídica' && (
        <div className="border-t pt-4">
          <h3 className="text-lg font-medium text-slate-900 mb-3">Representante Legal</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-xs font-semibold tracking-wider uppercase text-slate-500 block mb-1">
                Nombre Completo
              </label>
              <Input
                name="representante_legal_nombre"
                value={formData.representante_legal_nombre}
                onChange={handleChange}
              />
            </div>
            <div>
              <label className="text-xs font-semibold tracking-wider uppercase text-slate-500 block mb-1">
                Cédula
              </label>
              <Input
                name="representante_legal_cedula"
                value={formData.representante_legal_cedula}
                onChange={handleChange}
              />
            </div>
            <div>
              <label className="text-xs font-semibold tracking-wider uppercase text-slate-500 block mb-1">
                Cargo
              </label>
              <Input
                name="representante_legal_cargo"
                value={formData.representante_legal_cargo}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>
      )}

      {/* Responsabilidades Fiscales */}
      <div className="border-t pt-4">
        <h3 className="text-lg font-medium text-slate-900 mb-3">Responsabilidades Fiscales</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {responsabilidades.map((resp) => (
            <label key={resp.codigo} className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.responsabilidades_fiscales.includes(resp.codigo)}
                onChange={() => handleResponsabilidadToggle(resp.codigo)}
                className="w-4 h-4 mt-0.5"
              />
              <span className="text-sm text-slate-700">
                <strong>{resp.codigo}</strong> - {resp.nombre}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Perfil Tributario */}
      <div className="border-t pt-4">
        <h3 className="text-lg font-medium text-slate-900 mb-3">Perfil Tributario</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold tracking-wider uppercase text-slate-500 block mb-1">Periodicidad IVA</label>
            <select name="periodicidad_iva" value={formData.periodicidad_iva} onChange={handleChange}
              className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">
              <option value="">No aplica</option>
              <option value="Bimestral">Bimestral</option>
              <option value="Cuatrimestral">Cuatrimestral</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold tracking-wider uppercase text-slate-500 block mb-1">Periodicidad ICA</label>
            <select name="periodicidad_ica" value={formData.periodicidad_ica} onChange={handleChange}
              className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">
              <option value="">No aplica</option>
              <option value="Mensual">Mensual</option>
              <option value="Bimestral">Bimestral</option>
              <option value="Anual">Anual</option>
            </select>
          </div>
          <div className="md:col-span-2 flex flex-wrap gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={formData.es_agente_retencion} onChange={(e) => setFormData(prev => ({ ...prev, es_agente_retencion: e.target.checked }))} className="w-4 h-4" />
              <span className="text-sm text-slate-700">Agente de Retención</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={formData.declara_renta} onChange={(e) => setFormData(prev => ({ ...prev, declara_renta: e.target.checked }))} className="w-4 h-4" />
              <span className="text-sm text-slate-700">Declara Renta</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={formData.regimen_simple} onChange={(e) => setFormData(prev => ({ ...prev, regimen_simple: e.target.checked }))} className="w-4 h-4" />
              <span className="text-sm text-slate-700">Régimen Simple</span>
            </label>
          </div>
        </div>
      </div>

      {/* Estado */}
      <div>
        <label className="text-xs font-semibold tracking-wider uppercase text-slate-500 block mb-1">
          Estado *
        </label>
        <select
          name="estado"
          value={formData.estado}
          onChange={handleChange}
          className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
          required
        >
          <option value="Activo">Activo</option>
          <option value="Pausado">Pausado</option>
          <option value="Inactivo">Inactivo</option>
        </select>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button
          type="submit"
          className="bg-[#1F3864] hover:bg-[#172b4d] text-white"
          data-testid="submit-client-form"
        >
          {client ? 'Actualizar Cliente' : 'Crear Cliente'}
        </Button>
      </div>
    </form>
  );
};

export { ClientForm };
export default Clients;
